import React, { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

// Components

import UniversalInput from '@lib/wrappers/UniversalInput';

// Constants

import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { VALIDATION_TYPES }from '@constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { STATE_CODES } from '@constants/common/statesList';
import { omit } from '@utils/miscellaneous/objectHelpers';
import { getAddressLinePoBoxFlags } from '@utils/validators/customValidations';

const HomeAddressInputs = props => {

    const {
        areStreetLinesVisible = true,
        postalCode,
        countyCode,
        streetAddress1,
        streetAddress2,
        state,
        city,
        currentData,
        counties,
        outOfStateAttestation,
        stateCode,
        isInputStandalone = false,
        isState = {},
        ...restProps
    } = props;

    const cleanedRestProps = omit(restProps, 'children');
    const inputActionsWithoutOnBlur = omit(cleanedRestProps.inputActions, ['onFieldBlur', 'onFieldFocus']);
    const cleanedRestPropsWithoutOnBlur = {
        ...cleanedRestProps,
        inputActions: inputActionsWithoutOnBlur
    };

    const { line1HasPoBoxError, line2HasPoBoxError } = getAddressLinePoBoxFlags(streetAddress1, streetAddress2);

    return (
        <Fragment>
            {
                areStreetLinesVisible
                    ? (
                        <Fragment>
                            <UniversalInput
                                label = { {
                                    text: 'ssap.input.label.address_line_one',
                                    labelClasses: []
                                } }
                                inputData = { {
                                    isInputRequired: true,
                                    type: INPUT_TYPES.REGULAR_INPUT,
                                    name: 'streetAddress1',
                                    helperClasses: [],
                                    value: streetAddress1,
                                    placeholder: 'ssap.input.placeholder.address_line_one',
                                    validationType: VALIDATION_TYPES.HOME_ADDRESS_LINE1,
                                    valuesToMatch: { otherLineValue: streetAddress2, isState },
                                    validationErrorMessage: line1HasPoBoxError ? VALIDATION_ERROR_MESSAGES.PO_BOX_NOT_PERMITTED : undefined,
                                    currentData: currentData,
                                    fields: [ 'householdContact', 'homeAddress' ],
                                    isInputStandalone
                                } }
                                { ...cleanedRestProps }
                            />

                            <UniversalInput
                                label = { {
                                    text: 'ssap.input.label.address_line_two',
                                    labelClasses: []
                                } }
                                inputData = { {
                                    isInputRequired: false,
                                    type: INPUT_TYPES.REGULAR_INPUT,
                                    name: 'streetAddress2',
                                    helperClasses: [],
                                    value: streetAddress2,
                                    placeholder: 'ssap.input.placeholder.address_line_two',
                                    validationType: VALIDATION_TYPES.HOME_ADDRESS_LINE2,
                                    valuesToMatch: { otherLineValue: streetAddress1, isState },
                                    validationErrorMessage: line2HasPoBoxError ? VALIDATION_ERROR_MESSAGES.PO_BOX_NOT_PERMITTED : undefined,
                                    currentData: currentData,
                                    fields: [ 'householdContact', 'homeAddress' ],
                                    isInputStandalone
                                } }
                                { ...cleanedRestProps }
                            />
                        </Fragment>
                    )
                    : null
            }

            <UniversalInput
                label = { {
                    text: 'ssap.input.label.city',
                    labelClasses: []
                } }
                inputData = { {
                    isInputRequired: true,
                    type: INPUT_TYPES.REGULAR_INPUT,
                    name: 'city',
                    helperClasses: [],
                    value: city,
                    placeholder: 'ssap.input.placeholder.city',
                    validationType: VALIDATION_TYPES.CITY,
                    currentData: currentData,
                    fields: [ 'householdContact', 'homeAddress' ],
                    isInputStandalone
                } }
                { ...cleanedRestProps }
            />

            <UniversalInput
                label = { {
                    text: 'ssap.input.label.zip',
                    labelClasses: []
                } }
                inputData = { {
                    isInputRequired: true,
                    type: INPUT_TYPES.REGULAR_INPUT,
                    name: 'postalCode',
                    helperClasses: [],
                    value: postalCode,
                    placeholder: 'ssap.input.placeholder.zip',
                    validationType: VALIDATION_TYPES.ZIP_CODE_5_DIGITS,
                    currentData: currentData,
                    fields: [ 'householdContact', 'homeAddress' ]
                } }
                { ...cleanedRestProps }
            />

            <UniversalInput
                label = { {
                    text: 'ssap.input.label.state',
                    isLabelRequired: true
                } }
                inputData = { {
                    isInputRequired: true,
                    type: INPUT_TYPES.DROPDOWN,
                    name: 'state',
                    value: state,
                    options: DROPDOWN_OPTIONS.STATE,
                    validationType: VALIDATION_TYPES.CHOSEN,
                    currentData: JSON.parse(JSON.stringify(currentData)),
                    fields: [ 'householdContact', 'homeAddress' ]
                } }
                { ...cleanedRestPropsWithoutOnBlur }
            />

            <UniversalInput
                label = { {
                    text: 'ssap.input.label.county',
                    isLabelRequired: true
                } }
                inputData = { {
                    isInputRequired: true,
                    type: INPUT_TYPES.DROPDOWN,
                    name: 'countyCode',
                    value: countyCode,
                    options: DROPDOWN_OPTIONS.COUNTIES_FOR_EXACT_ZIPCODE(counties),
                    valuesToMatch: counties.map(county => Object.keys(county)[0]),
                    validationType: VALIDATION_TYPES.CHOSEN_FROM_LIST,
                    currentData: currentData,
                    fields: [ 'householdContact', 'homeAddress' ],
                    disabled: !postalCode
                } }
                { ...cleanedRestPropsWithoutOnBlur }
            />

            {
                // TODO: This input is supposed to be moved out of Home Address inputs to Page component
                stateCode !== state &&

                <Fragment>
                    <div className = 'grid-row' >
                        <div className = 'usa-alert gi-alert gi-alert__left-accent gi-alert__info' >
                            <div className = 'gi-alert__body' >
                                <p className = 'gi-alert__text' >
                                    <FormattedMessage id = 'ssap.input.label.out_of_state_information' />
                                </p>
                            </div>
                        </div>
                    </div>
                    <UniversalInput
                        label = { {
                            labelClass: [ 'required-true' ],
                            text: 'ssap.input.label.out_of_state_attestation',
                            labelTextReplacements: {
                                stateName: STATE_CODES.find(state => state.stateCode === stateCode).stateName
                            }
                        } }
                        inputData = { {
                            isInputRequired: true,
                            type: INPUT_TYPES.CHECKBOX,
                            name: 'outOfStateAttestation',
                            classes: [ 'tablet:grid-col-6 tablet:grid-offset-4 layout--02' ],
                            checked: outOfStateAttestation,
                            value: outOfStateAttestation,
                            validationType: VALIDATION_TYPES.CHECKED,
                            currentData: currentData,
                            fields: [ 'householdContact' ]
                        } }
                        { ...cleanedRestPropsWithoutOnBlur }
                    />
                </Fragment>
            }
        </Fragment>
    );
};

export default HomeAddressInputs;